<template>
  <v-card
    flat
    class="ma-3 pa-6"
  >
    <v-row align="center" class="pl-0 mb-4 pt-3 pr-3">
      <v-card-title>
        Audit Log
      </v-card-title>
    </v-row>

    <v-row class="px-3 mb-2" dense>
      <v-col cols="12" sm="6" md="3">
        <v-autocomplete
          v-model="filter.user_id"
          :items="users"
          item-value="id"
          :item-text="userText"
          label="User"
          multiple
          clearable
          clear-icon="fas fa-times"
          outlined
          dense
          hide-details
          @change="search()"
          @click:clear="search()"
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-autocomplete
          v-model="filter.model"
          :items="modelOptions"
          label="Model"
          multiple
          clearable
          clear-icon="fas fa-times"
          outlined
          dense
          hide-details
          @change="search()"
          @click:clear="search()"
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-select
          v-model="filter.action"
          :items="actionOptions"
          label="Action"
          multiple
          clearable
          clear-icon="fas fa-times"
          outlined
          dense
          hide-details
          @change="search()"
          @click:clear="search()"
        ></v-select>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-text-field
          v-model="filter.query"
          label="Search in data"
          append-icon="fas fa-search"
          clearable
          clear-icon="fas fa-times"
          outlined
          dense
          hide-details
          single-line
          @input="search()"
          @click:clear="search()"
        ></v-text-field>
      </v-col>
    </v-row>

    <v-data-table
      class="clickable"
      :headers="headers"
      :items="items"
      :options.sync="options"
      :server-items-length="totalCount"
      :footer-props="{ 'items-per-page-options': [10, 50, 100] }"
      :loading="isSearching"
      @click:row="rowClick"
    >

      <template v-slot:item.created_at="{ value }">
        {{ formatDateTime(value) }}
      </template>

      <template v-slot:item.user_name="{ value }">
        {{ value || '—' }}
      </template>

      <template v-slot:item.action="{ value }">
        <v-chip :color="actionColor(value)" small dark>{{ value }}</v-chip>
      </template>

      <template v-slot:item.model="{ value }">
        {{ shortModel(value) }}
      </template>

      <template v-slot:item.data_identifier="{ item }">
        {{ dataIdentifier(item.data) || '—' }}
      </template>

      <template v-slot:item.data_name="{ item }">
        <div class="text-truncate" style="max-width: 260px">{{ dataName(item.data) || '—' }}</div>
      </template>

    </v-data-table>

    <v-dialog
      v-model="dialog"
      max-width="800"
    >
      <v-card v-if="selected">
        <v-toolbar color="primary" dark flat short class="px-2">
          <v-toolbar-title>Audit entry #{{ selected.id }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn @click="dialog = false" icon><v-icon>fas fa-times</v-icon></v-btn>
        </v-toolbar>

        <v-card-text class="pt-6">
          <dl class="row table">
            <dt class="col-3 grey--text">When</dt>
            <dd class="col-9">{{ formatDateTime(selected.created_at) }}</dd>
            <dt class="col-3 grey--text">User</dt>
            <dd class="col-9">{{ selected.user_name || '—' }}</dd>
            <dt class="col-3 grey--text">Action</dt>
            <dd class="col-9">{{ selected.action }}</dd>
            <dt class="col-3 grey--text">Model</dt>
            <dd class="col-9">{{ shortModel(selected.model) }} <span class="grey--text">#{{ selected.model_id }}</span></dd>
            <template v-if="dataIdentifier(selected.data)">
              <dt class="col-3 grey--text">Identifier</dt>
              <dd class="col-9">{{ dataIdentifier(selected.data) }}</dd>
            </template>
            <template v-if="dataName(selected.data)">
              <dt class="col-3 grey--text">Name</dt>
              <dd class="col-9">{{ dataName(selected.data) }}</dd>
            </template>
          </dl>
          <div class="grey--text mt-4 mb-1">Data</div>
          <pre class="data-block">{{ prettyData(selected.data) }}</pre>
        </v-card-text>

        <v-card-actions class="px-6 pb-6">
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            class="px-5"
            large
            outlined
            depressed
            @click="dialog = false"
          >Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-card>

</template>

<script>
import { inject, ref, computed } from 'vue'
import { formatDateTime } from '@/utils'

export default {
  setup() {
    const api = inject('api')

    const auditLogCollection = api.collection('audit-logs', {
      params: {
        sort: '-id',
      },
      filter: {
        query: 'query:data',
        user_id: 'in',
        model: 'in',
        action: 'in',
      },
      store: true,
    })

    const { items, filter, totalCount, options, isSearching } = auditLogCollection
    const search = auditLogCollection.debounceSearch(300)

    // Filter option sources
    const { items: users } = api.collection('users').all()

    const models = ref([])
    api.client.get('audit-logs/models').then(res => { models.value = res.data || [] })

    const modelOptions = computed(() =>
      models.value
        .map(model => ({ text: shortModel(model), value: model }))
        .sort((a, b) => a.text.localeCompare(b.text))
    )

    const actionOptions = ['create', 'update', 'delete', 'restore', 'force-delete']

    const headers = [
      { text: 'When', value: 'created_at', width: '13%' },
      { text: 'User', value: 'user_name', width: '13%', sortable: false },
      { text: 'Action', value: 'action', width: '10%', sortable: false },
      { text: 'Model', value: 'model', width: '13%', sortable: false },
      { text: 'Identifier', value: 'data_identifier', width: '15%', sortable: false },
      { text: 'Name', value: 'data_name', width: '25%', sortable: false },
      { text: 'ID', value: 'model_id', width: '8%', sortable: false },
    ]

    const dialog = ref(false)
    const selected = ref(null)

    function userText(user) {
      return user ? (user.first_name || user.username || user.email) : ''
    }

    function rowClick(item) {
      selected.value = item
      dialog.value = true
    }

    function shortModel(model) {
      if (!model) return ''
      const parts = String(model).split('\\')
      return parts[parts.length - 1]
    }

    function actionColor(action) {
      switch (action) {
        case 'create': return 'green'
        case 'update': return 'blue'
        case 'delete': return 'orange'
        case 'force-delete': return 'red'
        case 'restore': return 'teal'
        default: return 'grey'
      }
    }

    function parseData(data) {
      if (data === null || data === undefined || data === '') return null
      try {
        return typeof data === 'string' ? JSON.parse(data) : data
      } catch (e) {
        return null
      }
    }

    const IDENTIFIER_KEYS = ['unique_identifier', 'code', 'type']
    const NAME_KEYS = ['name', 'description', 'unique_name', 'article_number', 'sku', 'brezo_internal_reference', 'image1_path']

    function pickFromData(data, keys) {
      const parsed = parseData(data)
      if (!parsed) return null
      for (const key of keys) {
        const value = parsed[key]
        if (value !== undefined && value !== null && value !== '') return value
      }
      return null
    }

    function dataIdentifier(data) {
      return pickFromData(data, IDENTIFIER_KEYS)
    }

    function dataName(data) {
      return pickFromData(data, NAME_KEYS)
    }

    function prettyData(data) {
      if (data === null || data === undefined || data === '') return ''
      const parsed = parseData(data)
      return parsed === null ? String(data) : JSON.stringify(parsed, null, 2)
    }

    return {
      items,
      filter,
      totalCount,
      options,
      isSearching,
      search,
      users,
      modelOptions,
      actionOptions,
      headers,
      dialog,
      selected,
      userText,
      rowClick,
      shortModel,
      actionColor,
      dataIdentifier,
      dataName,
      prettyData,
      formatDateTime,
    }

  },
}
</script>

<style scoped>

:deep(.v-data-table.clickable table tbody tr:not(.v-data-table__empty-wrapper):hover) {
  cursor: pointer;
}

dl.table dt, dl.table dd {
  padding-top: 2px;
  padding-bottom: 2px;
}

.data-block {
  background: #f5f5f5;
  border-radius: 4px;
  padding: 12px;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 400px;
  overflow: auto;
}

</style>
